/**
 * ⌘K filters (docs/ux/artboards/optimal, review row 14): `p:project`, `a:agent`, `since:2h`,
 * `is:running` narrow sessions and open asks; the rest of the text is the fuzzy query. Pure, so
 * the palette and its tests share one reading of a query.
 */
import type { RunSummary } from './types.js'

export interface PaletteFilters {
  p?: string
  a?: string
  since?: string
  is?: string
}
export interface PaletteQuery {
  text: string
  filters: PaletteFilters
  /** True once any `key:` prefix is typed, even before its value. */
  active: boolean
}

const KEYS = new Set(['p', 'a', 'since', 'is'])
const TOKEN = /(?:^|\s)(p|a|since|is):(?:"([^"]*)"|(\S*))/g

export function parsePaletteQuery(q: string): PaletteQuery {
  const filters: PaletteFilters = {}
  let active = false
  const text = q
    .replace(TOKEN, (_m, key: string, quoted: string | undefined, bare: string | undefined) => {
      active = true
      const v = (quoted ?? bare ?? '').trim()
      if (v && KEYS.has(key)) filters[key as keyof PaletteFilters] = v
      return ' '
    })
    .replace(/\s+/g, ' ')
    .trim()
  return { text, filters, active }
}

const UNIT: Record<string, number> = { m: 60_000, h: 3_600_000, d: 86_400_000, w: 7 * 86_400_000 }

/** `30m` `2h` `3d` `1w` `today` `yesterday` as the earliest timestamp to keep. */
export function sinceMs(spec: string, now: number): number | undefined {
  const s = spec.toLowerCase()
  if (s === 'today' || s === 'yesterday') {
    const d = new Date(now)
    d.setHours(0, 0, 0, 0)
    return d.getTime() - (s === 'yesterday' ? 86_400_000 : 0)
  }
  const m = /^(\d+)([mhdw])$/.exec(s)
  return m ? now - Number(m[1]) * (UNIT[m[2] as string] as number) : undefined
}

const has = (hay: string | undefined, needle: string) =>
  (hay ?? '').toLowerCase().includes(needle.toLowerCase())

type RunLike = Pick<RunSummary, 'status' | 'updatedAt' | 'projectName' | 'agent' | 'landing'>

const RUN_STATE: Record<string, (r: RunLike) => boolean> = {
  running: (r) => r.status === 'running' || r.status === 'compacting',
  waiting: (r) => r.status === 'waiting',
  needs: (r) => r.status === 'waiting',
  idle: (r) => r.status === 'idle',
  done: (r) => r.status === 'done',
  failed: (r) => r.status === 'error' || r.status === 'failed',
  landed: (r) => r.landing?.state === 'landed',
  live: (r) => ['running', 'waiting', 'compacting'].includes(String(r.status)),
}

export function runMatches(r: RunLike, f: PaletteFilters, now: number): boolean {
  if (f.p && !has(r.projectName, f.p)) return false
  if (f.a && !has(r.agent, f.a)) return false
  if (f.since) {
    const from = sinceMs(f.since, now)
    if (from !== undefined && (r.updatedAt ?? 0) < from) return false
  }
  if (f.is) {
    const test = RUN_STATE[f.is.toLowerCase()]
    if (test && !test(r)) return false
  }
  return true
}

export function askMatches(
  a: { projectName?: string | undefined; agentLabel?: string | undefined; createdAt: number },
  f: PaletteFilters,
  now: number,
): boolean {
  if (f.p && !has(a.projectName, f.p)) return false
  if (f.a && !has(a.agentLabel, f.a)) return false
  if (f.since) {
    const from = sinceMs(f.since, now)
    if (from !== undefined && a.createdAt < from) return false
  }
  if (f.is && !['needs', 'waiting', 'open', 'ask'].includes(f.is.toLowerCase())) return false
  return true
}
